import { FormEvent, useEffect, useState } from 'react';
import { addParamsToUrl } from '@zapier/url-utils';
import { urls } from '@zapier/identity-utils';

import { DASHBOARD_FALLBACK_URL_NAME } from '../../constants';
import { CodeType, RouteTo } from '../../types';
import { defaultRouteTo, fetch } from '../../utils';
import { storeAuthType } from '../../hooks';

// I'm not worthy of typescript's amazing power
type TwoFactorData = Partial<Record<CodeType, string>>;

export type TwoFactorStatus = 'initial' | 'submitting' | 'success';

type UseTwoFactorInput = {
  /**
   * The type of code the user is submitting
   */
  codeType: CodeType;
  /**
   * Url of page to redirect user to after successfully signing up
   */
  next?: string | undefined;
  /**
   * Routing callback to integrate with router. Will use window.href.location directly when not specified
   */
  routeTo?: RouteTo;
};

async function submitTwoFactor(
  data: TwoFactorData,
  next: string,
  routeTo: RouteTo
) {
  const isRecovery = data?.recovery_code;

  const resp = await fetch(urls.TWO_FACTOR_AUTH_API_URL, {
    method: 'POST',
    body: data,
    headers: {
      Accept: 'application/json',
    },
  });

  if (resp.status === 200) {
    // Yay! Success

    // If the user has successfully logged in via 2FA, store the user's authentication method, so we can automatically
    // show this authentication method next time they try to log in.
    storeAuthType('email');
    if (!next || next === urls.DEFAULT_LOGIN_NEXT) {
      routeTo(urls.DEFAULT_LOGIN_NEXT);
    } else {
      const redirectUrl = addParamsToUrl(urls.REDIRECT_URL, {
        next: next,
        safe_only: true,
        fallback: DASHBOARD_FALLBACK_URL_NAME,
      });
      routeTo(redirectUrl);
    }
    return {
      didRedirect: true,
    };
  } else if (resp.status === 401) {
    return {
      didRedirect: false,
      error: 'Not authenticated',
    };
  } else if (resp.status === 403) {
    return {
      didRedirect: false,
      error: isRecovery
        ? 'That recovery code doesn’t look right, or it has been used before.'
        : 'That code doesn’t look right.',
    };
  } else if (resp.status == 429) {
    return {
      didRedirect: false,
      error: 'You’re trying too frequently.',
    };
  } else {
    // Unknown error
    return {
      didRedirect: false,
      error:
        'Yikes! Something went wrong. If you keep having trouble, please contact support.',
    };
  }
}

export function useTwoFactor({
  codeType = 'code',
  next = urls.DEFAULT_LOGIN_NEXT,
  routeTo = defaultRouteTo,
}: UseTwoFactorInput) {
  const [code, setCode] = useState<string>('');
  const [error, setError] = useState<string | undefined>();
  const [status, setStatus] = useState<TwoFactorStatus>('initial');

  // If the code type changes reset the form
  useEffect(() => {
    setCode('');
  }, [codeType]);

  async function handleSubmit(e?: FormEvent<HTMLFormElement>) {
    if (e) {
      e.preventDefault();
    }

    setStatus('submitting');
    setError(undefined);

    const result = await submitTwoFactor(
      {
        [codeType]: code,
      },
      next,
      routeTo
    );

    setError(result.error);
    setStatus(result.error ? 'initial' : 'success');
  }

  function handleChangeCode(value: string) {
    setCode(value);
  }

  return {
    // Is the form allowed to be submitted
    canSubmit: Boolean(code),
    // Current field value
    code,
    // Type of code user is going to submit
    codeType,
    // Error returned from API
    error,
    // Callback to handle changes to form data
    handleChangeCode,
    // Callback to handle submitting form data to API
    handleSubmit,
    // Form status
    status,
  };
}
